<template>
    <div v-if="selectedResourceDefinition" class="category-allocation-area">
        <div class="category-allocation-area__title">
            <label class="font-weight-bold">
                {{ toSentenceCase($t('preparation.allocation.categoryAllocation')) }}
            </label>
        </div>
        <promo-ag-grid
            :row-data="rowData"
            :column-defs="columnDefs"
            :grid-options="gridOptions"
            :default-col-def="defaultColDef"
            dom-layout="autoHeight"
            @grid-ready="onGridReady"
        />
    </div>
</template>

<script>
import categoryAllocationsTypes from '@enums/category-allocations-types';
import featureFlags from '@enums/feature-flags';
import { mapGetters, mapState, mapMutations } from 'vuex';
import { filter, map, includes, uniq, flattenDeep, get } from 'lodash';
import { markRaw } from 'vue';
import { toSentenceCase } from '@/js/utils/string-utils';
import vuexComponentMixin from '@/js/mixins/vuex-component';
import CategoriesSelectCellRenderer from './categories-select-cell-renderer.vue';
import CategoriesViewCellRenderer from './categories-view-cell-renderer.vue';
import CategoriesInfoHeader from './categories-info-header.vue';

const { canEditChannel } = featureFlags;

export default {
    mixins: [vuexComponentMixin],
    props: {},
    data() {
        return {
            categoryAllocationsTypes,
            canEditChannel,
            gridApi: null,
            defaultColDef: {
                suppressMovable: true,
                lockPinned: true,
                sortable: false,
                resizable: false,
                filter: false,
                suppressHeaderMenuButton: true,
                menuTabs: [],
            },
            gridOptions: markRaw({
                rowHeight: 35,
                headerHeight: 46,
                suppressHorizontalScroll: true,
                rowClassRules: {
                    'allocation-area__row--highlighted': ({ data }) =>
                        this.highlightedCategory === data?.categoryKey,
                },
            }),
            columnDefs: [
                {
                    headerName: toSentenceCase(this.$t('preparation.allocation.categoryHeader')),
                    field: 'categoryName',
                    flex: 1,
                },
                {
                    headerName: '',
                    cellRenderer: CategoriesSelectCellRenderer,
                    cellRendererParams: {
                        isCategoryDisabled: () => this.isCategoryDisabled(),
                        onSelect: categoryKey => this.setCategory({ categoryKey }),
                    },
                    width: 120,
                    cellClass: 'flex-cell-start',
                },
                {
                    headerName: toSentenceCase(this.$t('preparation.allocation.thisPage')),
                    field: 'countPage',
                    cellClass: ['font-weight-bold', 'ag-right-aligned-cell'],
                    width: 100,
                    valueFormatter: ({ value, node }) =>
                        node.rowPinned
                            ? ''
                            : this.$n('numbers.default.numberWithDecimals', value ?? 0),
                },
                {
                    headerName: toSentenceCase(this.$t('preparation.allocation.total')),
                    field: 'countAllLeaflet',
                    cellClass: ['font-weight-bold', 'ag-right-aligned-cell'],
                    width: 100,
                    valueFormatter: ({ value, node }) =>
                        node.rowPinned
                            ? ''
                            : this.$n('numbers.default.numberWithDecimals', value ?? 0),
                },
                {
                    headerName: toSentenceCase(this.$t('preparation.allocation.percentSymbol')),
                    field: 'countAllPercent',
                    // headerClass: 'ag-right-aligned-header',
                    cellClass: ['font-weight-bold', 'ag-right-aligned-cell'],
                    width: 100,
                    valueFormatter: ({ value, node }) =>
                        node.rowPinned ? '' : this.$n('numbers.default.percentage', value ?? 0),
                },
                {
                    headerName: '',
                    headerComponent: CategoriesInfoHeader,
                    headerComponentParams: {
                        tooltipText: toSentenceCase(
                            this.$t('preparation.allocation.categoryAllocationExplanation', {
                                thisPage: toSentenceCase(
                                    this.$t('preparation.allocation.thisPage')
                                ),
                                total: toSentenceCase(this.$t('preparation.allocation.total')),
                                percentSymbol: this.$t('preparation.allocation.percentSymbol'),
                            })
                        ),
                    },
                    cellRenderer: CategoriesViewCellRenderer,
                    cellRendererParams: {
                        onView: categoryKey => this.setHighlightedCategoryAction({ categoryKey }),
                    },
                    width: 80,
                    cellClass: 'flex-cell-start',
                },
            ],
        };
    },

    computed: {
        ...mapGetters('subCampaigns', [
            'selectedSubCampaign',
            'selectedResourceDefinition',
            'selectedResourceDefinitionPage',
        ]),
        ...mapGetters('context', ['userCategories']),
        ...mapGetters('promotions', ['getPromotionById']),
        ...mapGetters('scenarios', ['getScenarioById']),
        ...mapGetters('products', ['getProductsCategories']),
        ...mapState('clientConfig', ['resourceTemplateLayouts']),
        ...mapState('subCampaigns', [
            'selectedResourceDefinitionPageNumber',
            'highlightedCategory',
            'areaForCategoryAllocationSelected',
        ]),
        categories() {
            // Get categories that have been planned on the sub-campaign, of which
            // the user has permissions to see.
            const subCampaignCategories = filter(this.userCategories, category => {
                if (includes(this.selectedSubCampaign.categories, category.levelEntryKey)) {
                    return category;
                }
            });

            return map(subCampaignCategories, subCampaignCategory => {
                return {
                    categoryKey: subCampaignCategory.levelEntryKey,
                    categoryName: subCampaignCategory.levelEntryDescription,
                };
            });
        },
        // We need to count selected leaflet capacity
        // Each page of leaflet should have the template for - layoutTemplate
        // layout is an array like this:
        // [ 'a a b', 'a a c', 'a a d'] for this array capacity is 4 - a, b, c, d
        currentLeafletSpaces() {
            let result = 0;
            if (this.selectedResourceDefinition) {
                result = this.selectedResourceDefinition.pages.reduce((acc, page) => {
                    const { layoutTemplate } = page;
                    if (layoutTemplate) {
                        const { layout } = this.resourceTemplateLayouts.find(
                            layoutItem => layoutItem.key === layoutTemplate
                        );
                        acc += uniq(flattenDeep(layout.map(layoutStr => layoutStr.split(' '))))
                            .length;
                    }
                    return acc;
                }, 0);
            }
            return result;
        },
        // For each category counting how many times this
        // category placed on each page and in full leaflet
        categoryCounts() {
            const result = {};
            if (this.selectedResourceDefinition) {
                this.selectedResourceDefinition.pages.forEach(page => {
                    page.assignment.forEach(assignmentItem => {
                        let productCategories;
                        const assignmentItemCopy = { ...assignmentItem };

                        if (!assignmentItemCopy.categoryKey && assignmentItemCopy.promotionId) {
                            // If a categoryKey has not been allocated, then retrieve
                            // the categories of the products that are associated with
                            // the allocated promotion.
                            const allocatedPromotion = this.getPromotionById(
                                assignmentItemCopy.promotionId
                            );
                            const products = get(allocatedPromotion, 'products', []);
                            productCategories = this.getProductsCategories(products);
                        }

                        const fallbackProductCategories = productCategories
                            ? [...productCategories]
                            : [];

                        // If a category has been explicitly allocated, then use it, otherwise
                        // fall-back to the promotions product categories.
                        const categories = assignmentItemCopy.categoryKey
                            ? [assignmentItemCopy.categoryKey]
                            : fallbackProductCategories;

                        const categoryCountProportion = categories.length
                            ? 1 / categories.length
                            : 0;

                        categories.forEach(categoryKey => {
                            if (!result[categoryKey]) {
                                result[categoryKey] = {
                                    totalCount: categoryCountProportion,
                                    pageCounts: {
                                        [page.pageNumber]: categoryCountProportion,
                                    },
                                };
                            } else {
                                result[categoryKey].totalCount += categoryCountProportion;
                                if (result[categoryKey].pageCounts[page.pageNumber]) {
                                    result[categoryKey].pageCounts[
                                        page.pageNumber
                                    ] += categoryCountProportion;
                                } else {
                                    result[categoryKey].pageCounts[
                                        page.pageNumber
                                    ] = categoryCountProportion;
                                }
                            }
                        });
                    });
                });
            }

            return result;
        },
        rowData() {
            return this.categories.map(category => ({
                categoryKey: category.categoryKey,
                categoryName: category.categoryName,
                countPage: this.countPage(category.categoryKey),
                countAllLeaflet: this.countAllLeaflet(category.categoryKey),
                countAllPercent: this.countAllPercent(category.categoryKey),
            }));
        },
        nonAllocatedRowData() {
            return [
                {
                    categoryKey: categoryAllocationsTypes.nonAllocated,
                    categoryName: toSentenceCase(this.$t('preparation.allocation.nonAllocated')),
                    countPage: this.countPage(categoryAllocationsTypes.nonAllocated),
                    countAllLeaflet: this.countAllLeaflet(categoryAllocationsTypes.nonAllocated),
                    countAllPercent: this.countAllPercent(categoryAllocationsTypes.nonAllocated),
                },
            ];
        },
    },
    watch: {
        highlightedCategory() {
            if (this.gridApi) {
                this.gridApi.redrawRows();
            }
        },
        nonAllocatedRowData(newData) {
            if (this.gridApi) {
                this.gridApi.setGridOption('pinnedBottomRowData', newData);
            }
        },
    },
    methods: {
        ...mapMutations('subCampaigns', ['setHighlightedCategory']),

        toSentenceCase,

        onGridReady({ api }) {
            this.gridApi = api;
            api.setGridOption('pinnedBottomRowData', this.nonAllocatedRowData);
        },
        isCategoryDisabled() {
            return this.isReadOnly || !this.areaForCategoryAllocationSelected;
        },
        setCategory({ categoryKey }) {
            this.emitter.emit('set-category', categoryKey);
        },
        countPage(category) {
            if (this.selectedResourceDefinitionPageNumber && this.categoryCounts[category]) {
                return this.categoryCounts[category].pageCounts[
                    this.selectedResourceDefinitionPageNumber
                ];
            }
            return 0;
        },
        countAllLeaflet(category) {
            if (this.categoryCounts[category]) {
                return this.categoryCounts[category].totalCount;
            }
            return 0;
        },
        countAllPercent(category) {
            return this.countAllLeaflet(category) / this.currentLeafletSpaces;
        },
        setHighlightedCategoryAction({ categoryKey }) {
            if (this.highlightedCategory && this.highlightedCategory === categoryKey) {
                this.setHighlightedCategory(null);
            } else {
                this.setHighlightedCategory(categoryKey);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.category-allocation-area {
    padding-bottom: 0.5rem;
    font-size: 1.2rem;
    padding-left: 1rem;
    z-index: $promo-details-z-index;

    &__title {
        padding-bottom: 0.5rem;
    }
}

:deep(.ag-header-cell) {
    background-color: $promo-table-blue-bg-colour-3 !important;
    @include promo-component-border($top: true);
}

:deep(.ag-floating-bottom .ag-cell) {
    font-weight: bold;
    background-color: white !important;
}

:deep(.allocation-area__row--highlighted) {
    background-color: $highlighted-category-bg-colour !important;
}

:deep(.ag-root-wrapper) {
    min-height: auto;
}
</style>
